import React, {
	type ChangeEventHandler,
	type KeyboardEventHandler,
	type FormEventHandler,
	type MouseEventHandler,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import {
	COLUMN_ID_MERGED_COLUMN,
	COLUMN_ID_CHECKBOX,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { InlineCreateAdditionalFieldsEntrypoint } from '@atlassian/jira-async-inline-create-additional-fields/entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type {
	IssueToCreate,
	IssueCreateIssueType,
	IssueCreateProject,
	ContextualFields,
} from '../../common/types.tsx';

import {
	IssueCreateContextualFieldsData,
	useIssueCreateContextualFields,
} from '../../controllers/issue-create-contextual-fields-data/index.tsx';
import type IssueTypeDropdownEntrypoint from './issue-type-dropdown/entrypoint.tsx';
import { IssueTypeDropdown } from './issue-type-dropdown/index.tsx';
import messages from './messages.tsx';
import ReturnIcon from './return-icon/index.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useInlineIssueCreateEntryPoint } from './utils';

const ICON_WIDTH = 24;
const PADDING = 8;
const HIERARCHY_LEVEL_PADDING = 12;

export const useIsReady = () => {
	const isReadyRef = useRef(false);
	useEffect(() => {
		setTimeout(() => {
			isReadyRef.current = true;
		}, 100);
	}, []);

	return isReadyRef;
};

const isComposing = (event: React.KeyboardEvent) => event.nativeEvent.isComposing;

export const IssueTypeDropdownFallback = () => (
	<IssueTypeDropdown project={null} onChange={noop} onProjectChange={noop} selectedType={null} />
);

type Appearance = 'row' | 'footer';

type Column = { id: string; defaultWidth?: number; mergedColumns?: { id: string }[] };

type InlineCreateFormProps = {
	initialIssueDraft?: Partial<IssueToCreate>;
	onDraftUpdate?: (issueDraft: Partial<IssueToCreate>) => void;
	onCreateIssue: (issue: IssueToCreate, contextualFields: ContextualFields) => void;
	onCancel?: () => void;
	appearance?: Appearance;
	entryPointSubject: EntryPointReferenceSubject<typeof IssueTypeDropdownEntrypoint>;
	depth: number;
	columns?: Column[];
	rankFieldId: string | null | undefined;
	isGrouping?: boolean;
	additionalFieldsEntryPointSubject?: EntryPointReferenceSubject<
		typeof InlineCreateAdditionalFieldsEntrypoint
	> | null;
};

const InlineCreateForm = ({
	initialIssueDraft,
	onDraftUpdate,
	onCreateIssue,
	onCancel,
	entryPointSubject,
	appearance = 'row',
	depth = 0,
	columns,
	isGrouping,
	additionalFieldsEntryPointSubject,
}: InlineCreateFormProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [textContent, setTextContent] = useState(initialIssueDraft?.summary ?? '');
	const [selectedType, setSelectedType] = useState<IssueCreateIssueType | null>(
		initialIssueDraft?.issueType ?? null,
	);
	const [selectedProject, setSelectedProject] = useState<IssueCreateProject | null>(
		initialIssueDraft?.project ?? null,
	);

	useEffect(() => {
		onDraftUpdate?.({
			summary: textContent,
			project: selectedProject ?? undefined,
			issueType: selectedType ?? undefined,
		});
	}, [onDraftUpdate, selectedProject, selectedType, textContent]);

	const formRef = useRef<HTMLFormElement | null>(null);
	const isEmptyRef = useRef(true);
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
	isEmptyRef.current = textContent.trim().length === 0;
	const contextualFields = useIssueCreateContextualFields();

	// prevent blurring from occurring when the form is first rendered
	const isReadyRef = useIsReady();

	const handleCreateIssue = useCallback(() => {
		if (!isEmptyRef.current && selectedType && selectedProject) {
			onCreateIssue(
				{
					summary: textContent,
					project: selectedProject,
					issueType: selectedType,
				},
				contextualFields,
			);
			setTextContent('');
		}
	}, [contextualFields, onCreateIssue, selectedProject, selectedType, textContent]);

	const handleCreateIssueRef = useRef(handleCreateIssue);
	handleCreateIssueRef.current = handleCreateIssue;

	const handleBlur = useCallback(
		(event: MouseEvent) => {
			if (!isReadyRef.current) {
				return;
			}
			// make sure to make a local copy of the isEmptyRef value before it gets reset by the create handler
			const formEmpty = isEmptyRef.current;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const inForm = formRef.current?.contains(event.target as Node) ?? false;

			if (!inForm) {
				onCancel?.();
				fireUIAnalytics(createAnalyticsEvent({}), 'form blurred', 'inlineCreate', {
					formEmpty,
					escapeKeyPressed: false,
				});
			}
		},
		[isReadyRef, onCancel, createAnalyticsEvent],
	);

	const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((e) => {
		const content = e.target.value.replace(/[\n\r]+/g, ' ');
		setTextContent(content);
	}, []);

	const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
		(e) => {
			if (e.key === 'Enter' && !isComposing(e)) {
				e.preventDefault();
				handleCreateIssue();
			}
		},
		[handleCreateIssue],
	);

	const handleFormKeyDown = useCallback<KeyboardEventHandler<HTMLFormElement>>((e) => {
		e.stopPropagation();
	}, []);

	const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault();
			handleCreateIssue();
		},
		[handleCreateIssue],
	);

	const handleCreateButtonClick = useCallback<MouseEventHandler<HTMLElement>>(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'inlineCreateSubmit');
	}, [createAnalyticsEvent]);

	useEffect(() => {
		// on mount, we want to move the cursor to the end of the input
		// TODO; Add test for this feature once we support pre-population of the summary
		textAreaRef.current?.setSelectionRange(
			textAreaRef.current.value.length,
			textAreaRef.current.value.length,
		);
		fireUIAnalytics(createAnalyticsEvent({}), 'form focused', 'inlineCreate');
	}, [createAnalyticsEvent]);

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			e.stopPropagation();
			if (e.key === 'Escape') {
				onCancel?.();
				fireUIAnalytics(createAnalyticsEvent({}), 'form blurred', 'inlineCreate', {
					formEmpty: isEmptyRef.current,
					escapeKeyPressed: true,
				});
			}
			if (e.key === 'Enter') {
				e.preventDefault();
				handleCreateIssue();
			}
		},
		[createAnalyticsEvent, handleCreateIssue, onCancel],
	);

	useEffect(() => {
		if (!__SERVER__) {
			document.addEventListener('click', handleBlur, true);
			document.addEventListener('keydown', handleEscape, true);

			return () => {
				document.removeEventListener('click', handleBlur, true);
				document.removeEventListener('keydown', handleEscape, true);
			};
		}
	}, [handleBlur, handleEscape]);

	const padding = fg('jira_list_merged_column_gate') ? HIERARCHY_LEVEL_PADDING : PADDING;
	let paddingLeft = depth > 1 ? (depth - 1) * ICON_WIDTH + padding : padding;

	const mergedColumn = fg('jira_list_merged_column_gate')
		? columns?.find(({ id }) => id === COLUMN_ID_MERGED_COLUMN)
		: undefined;

	if (mergedColumn && !isGrouping) {
		const checkboxColumnWidth =
			columns?.find(({ id }) => id === COLUMN_ID_CHECKBOX)?.defaultWidth ?? 0;

		const hasMergedFields = Number(mergedColumn.mergedColumns?.length) > 0;

		paddingLeft += (hasMergedFields ? ICON_WIDTH : 0) + checkboxColumnWidth - PADDING * depth;
	}

	const shouldShowAdditionalFields = useCallback(
		() => expVal('jira_powerful_inline_create_for_nin', 'isEnabled', false),
		[],
	);

	return (
		// TODO: Check if we can catch the escape in a different level that removes below lint rule
		<FullContent
			ref={formRef}
			onKeyDown={handleFormKeyDown}
			onSubmit={handleSubmit}
			appearance={appearance}
			paddingLeft={paddingLeft}
		>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointSubject}
				id="async-issue-create-issue-type-dropdown"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				fallback={<IssueTypeDropdownFallback />}
				runtimeProps={{
					onChange: setSelectedType,
					onProjectChange: setSelectedProject,
					selectedType,
				}}
			/>
			<Box
				xcss={TextAreaStyles}
				as="textarea"
				autoFocus
				aria-label={formatMessage(messages.placeholderText)}
				placeholder={formatMessage(messages.placeholderText)}
				value={textContent}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				ref={textAreaRef}
			/>

			{additionalFieldsEntryPointSubject != null &&
				selectedProject?.key &&
				fg('jira_render_pic_in_nin') && (
					<JiraEntryPointContainer
						entryPointReferenceSubject={additionalFieldsEntryPointSubject}
						id="async-inline-create-additional-fields"
						packageName="async-inline-create-additional-fields"
						teamName="jira-werewolves"
						fallback={null}
						runtimeProps={{
							projectKey: selectedProject?.key,
							fullWidth: false,
							isCompactDueDatePicker: true,
							shouldRenderToParent: true,
							shouldShowNameLabel: false,
							strategy: 'fixed',
							shouldShowAdditionalFields,
						}}
					/>
				)}
			<Flex gap="space.100" alignItems="center" justifyContent="end">
				<StyledCreateButton
					appearance="primary"
					iconAfter={<ReturnIcon />}
					isDisabled={isEmptyRef.current}
					spacing="compact"
					type="submit"
					onClick={handleCreateButtonClick}
				>
					{formatMessage(messages.createButton)}
				</StyledCreateButton>
			</Flex>
		</FullContent>
	);
};

export type Props = InlineCreateFormProps & {
	contextualFields?: ContextualFields;
};

const InlineCreateFormWithContainer = ({ contextualFields, ...props }: Props) => (
	<JSErrorBoundary fallback="flag" id="issue-table-inline-issue-create">
		<IssueCreateContextualFieldsData payload={contextualFields}>
			<InlineCreateForm {...props} />
		</IssueCreateContextualFieldsData>
	</JSErrorBoundary>
);

export default memo(InlineCreateFormWithContainer);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullContent = styled.form<
	Pick<InlineCreateFormProps, 'appearance'> & { paddingLeft: number }
>(
	{
		display: 'flex',
		gap: token('space.100'),

		backgroundColor: token('elevation.surface'),
		transition: 'box-shadow 200ms ease-in-out',
		boxShadow: `inset 0 0 0 ${token('border.width.outline')} ${token('color.border.focused')}`,
		boxSizing: 'border-box',
		outline: 'none',
		minHeight: '40px',
		flexDirection: 'row',
		alignItems: 'center',
		height: '40px',
		margin: '0px', // margin: '0px' is needed to override the default margin of the form element
		paddingTop: token('space.050'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		paddingLeft: (props) => `${props.paddingLeft}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	(props) =>
		props.appearance === 'footer'
			? {
					borderBottomLeftRadius: token('border.radius.200'),
					borderBottomRightRadius: token('border.radius.200'),
				}
			: {
					borderRadius: token('border.radius.100'),
				},
);

const TextAreaStyles = xcss({
	flex: '1',
	border: 'none',
	outline: 'none',
	margin: '0',
	padding: 'space.050',
	height: '20px',
	resize: 'none',
	backgroundColor: 'elevation.surface',
	color: 'color.text',
	font: 'font.body',
	scrollbarWidth: 'none',
	whiteSpace: 'pre',
	boxSizing: 'content-box',
	fontFamily: 'inherit',
});

// TODO: Maybe we can rewrite to pressable....
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCreateButton = styled(Button)({
	flex: 'none',
});
